/* eslint-disable no-console */
import React, {useEffect, useState} from 'react'
import {
 Navigate,
 useNavigate,
 useParams,
} from 'react-router-dom'
import {PageTitle} from 'layout/master-layout'
// import {toast} from 'react-toastify'
import {ITab, KTTab} from 'helpers/ui-components/KTTab'
import Spinner from 'layout/common/Spinner'
import {useAuth} from 'context/AuthProvider'
import {IOrder} from 'models/OrderManagement'
import {
 fetchOrderList,
 fetchSaleOrderSummary,
} from 'utils/api/auth/orderManagementService'
import {useTranslation} from 'react-i18next'
import {IFormField, ISorting} from 'models/Common'
import {KTSummaryCard} from 'helpers/ui-components/KTSummaryCard'
import {
 formatNumberForDisplay,
 generateRandomNo,
} from 'utils/helper'
import {KTFilterBox} from 'helpers/ui-components/KTFilterBox'
import OrderTable from './OrderTable'
import {fetchFilterConfig} from 'utils/api/auth/commonService'
import CustomerDetails from './CustomerDetails'
import OrderActions from './OrderActions'
import {createFilterPayload} from 'utils/helpers/genericFilterForm'
import {
 DEFAULT_TRADE_TAB,
 getTradeListPath,
 getTradeOrderPath,
 isValidTradeTab,
 TradeTabId,
} from './tradeRoutes'
import {normalizeOrderRecord} from './tradeNegotiationUtils'

const ORDER_STATUS = {
 open_orders: 'PENDING',
 accepted_orders: 'CONFIRMED',
 dispatched_orders: 'SHIPPED',
 pending_payment_orders: 'PAYMENT_CONFIRMATION',
 completed_orders: 'COMPLETE',
 rejected_cancelled_orders: 'REJECTED_CANCELLED_ORDER',
}

type OrderManagementPageProps = {
 activeTab: TradeTabId
}

const OrderManagementPage = ({
 activeTab,
}: OrderManagementPageProps) => {
 const {t} = useTranslation()
 const navigate = useNavigate()
 const {userData} = useAuth()
 const [loader, setLoader] = useState(false)
 const [actionDetails, setActionDetails] =
  useState<IFormField | null>(null)
 const [currentPage, setCurrentPage] = useState(1)
 const [totalRecords, setTotalRecords] = useState(0)
 const [itemsPerPage, setItemsPerPage] = useState(25)
 const [filterCount, setFilterCount] = useState(0)
 const [sortConfig, setSortConfig] = useState<ISorting>({
  key: null,
  direction: 'ascending',
 })
 const [aggregatorId, setAggregatorId] = useState<
  number | null
 >(null)
 const [filterFormData, setFilterFormData] =
  useState<IFormField | null>(null)
 const [filterConfig, setFilterConfig] = useState<
  IFormField[]
 >([])
 const [orderList, setOrderList] = useState<IOrder[]>([])
 const [tabList, setTabList] = useState<ITab[]>([])
 const [orderSummaryList, setOrderSummaryList] = useState<
  ITab[]
 >([])

 const keyList = [
  {
   title: t('orderManagement.openOrders'),
   id: 'open_orders',
   icon: '',
   count: 0,
  },
  {
   title: t('orderManagement.acceptedOrders'),
   id: 'accepted_orders',
   icon: '',
  },
  {
   title: t('orderManagement.dispatchedOrders'),
   id: 'dispatched_orders',
   icon: '',
  },
  {
   title: t('orderManagement.pendingPaymentOrders'),
   id: 'pending_payment_orders',
   icon: '',
  },
  {
   title: t('orderManagement.completedOrders'),
   id: 'completed_orders',
   icon: '',
  },
  {
   title: t('orderManagement.rejectedOrders'),
   id: 'rejected_cancelled_orders',
   icon: '',
  },
 ]

 const orderSummaryCardList = [
  {
   title: t('orderManagement.openOrders'),
   id: 'open_orders',
   icon: '',
   count: 0,
  },
  {
   title: t('orderManagement.acceptedOrders'),
   id: 'accepted_orders',
   icon: '',
  },
  {
   title: t('orderManagement.dispatchedOrders'),
   id: 'dispatched_orders',
   icon: '',
  },
  {
   title: t('orderManagement.pendingPaymentOrders'),
   id: 'pending_payment_orders',
   icon: '',
  },
  {
   title: t('orderManagement.completedOrders'),
   id: 'completed_orders',
   icon: '',
  },
  {
   title: t('orderManagement.rejectedOrders'),
   id: 'rejected_cancelled_orders',
   icon: '',
  },
 ]

 const cardColor = [
  '#FFF3C2',
  '#E3D7F5',
  '#FDC',
  '#E3F5CE',
  '#CED8E0',
 ]
 const categoryList = [
  {
   key: '',
   value: 'All',
  },
  {
   key: 'FPO',
   value: 'FPO',
  },
  {
   key: 'BLFC',
   value: 'BLFC',
  },
  {
   key: 'PACS',
   value: 'PACS',
  },
 ]
 const getFilterConfig = async () => {
  setLoader(true)
  const res = await fetchFilterConfig({
   filter_type: 'ORDER_HISTORY',
   organisation_type: 'PMU',
  })
  if (res) {
   setFilterConfig(res?.applicableFilters ?? [])
  }
  setLoader(false)
 }

 const getOrderSummary = async (
  pageNo: number,
  status: string
 ) => {
  setLoader(true)
  const payload = await createFilterPayload(
   filterConfig,
   filterFormData
  )
  setFilterCount(payload.totalAppliedFilters)
  delete payload.totalAppliedFilters
  const res = await fetchOrderList({
   //    distributor_id: userData?.userId,
   order_status:
    ORDER_STATUS[status as keyof typeof ORDER_STATUS],
   page: pageNo,
   page_size: itemsPerPage,
   ...payload,
   ...(sortConfig.key && {
    sort_by: `${sortConfig.key}/${sortConfig.direction}`,
   }),
  })
  if (res) {
   const result: IFormField = res.results
   keyList.map((val: ITab) => {
    val.count = result?.[`${val.id}_count`]
   })
   setTabList(keyList)
   setOrderList(
    (result.results ?? []).map((order: IOrder) =>
     normalizeOrderRecord(order)
    )
   )
   setTotalRecords(res?.count ?? 0)
  }
  setLoader(false)
 }

 const getOrderSaleSummary = async () => {
  const payload = await createFilterPayload(
   filterConfig,
   filterFormData
  )
  const res = await fetchSaleOrderSummary({
   ...payload,
  })
  if (res) {
   const result: IFormField = res.data
   orderSummaryCardList.map((val: ITab) => {
    val.value = result?.[val.id]
   })
   setOrderSummaryList(orderSummaryCardList)
  }
 }

 const handleActionBtnClick = (data: IFormField | null) => {
  setActionDetails({
   ...data,
   status: data?.status,
  })
 }

 const handleTabClick = (index: string) => {
  if (activeTab !== index && isValidTradeTab(index)) {
   setCurrentPage(1)
   navigate(getTradeListPath(index))
  }
 }

 const handlePageChange = async (pageNo: number) => {
  setCurrentPage(pageNo)
  await getOrderSummary(pageNo, activeTab)
 }

 useEffect(() => {
  if (currentPage !== 1) {
   setCurrentPage(1)
  }
  void getOrderSummary(1, activeTab)
 }, [itemsPerPage])

 const reloadTableData = async () => {
  await getOrderSummary(currentPage, activeTab)
  await getOrderSaleSummary()
 }

 const handleSelectOrder = (order: IFormField | null) => {
  if (order?.orderId) {
   navigate(getTradeOrderPath(activeTab, order.orderId), {
    state: {
     actionsAllowed: order.actionsAllowed,
    },
   })
  }
 }

 const applyCategoryFilter = async (
  aggregatorType: string
 ) => {
  setFilterFormData((prevState: IFormField) => ({
   ...prevState,
   aggregator_type: aggregatorType,
  }))
 }

 useEffect(() => {
  void getFilterConfig()
 }, [])

 useEffect(() => {
  setCurrentPage(1)
  void getOrderSummary(1, activeTab)
 }, [activeTab])

 useEffect(() => {
  if (filterFormData && filterConfig.length > 0) {
   if (currentPage !== 1) {
    setCurrentPage(1)
   }
   void getOrderSummary(1, activeTab)
   void getOrderSaleSummary()
  }
 }, [filterFormData, filterConfig])

 useEffect(() => {
  if (sortConfig.key) {
   if (currentPage !== 1) {
    setCurrentPage(1)
   }
   void getOrderSummary(1, activeTab)
  }
 }, [sortConfig])

 return (
  <>
   {loader && <Spinner />}
   <CustomerDetails
    loggedInUserId={userData?.userId}
    aggregatorId={aggregatorId}
    setAggregatorId={setAggregatorId}
    setLoader={setLoader}
   />

   {
    <OrderActions
     loggedInUserId={userData?.userId}
     actionDetails={actionDetails}
     reloadTableData={reloadTableData}
     setActionDetails={setActionDetails}
     setLoader={setLoader}
    />
   }

   <div className='w-full min-h-full'>
    <KTFilterBox
     parentId={'ORDER_HISTORY'}
     btnText={t('helperText.filter')}
     dateRangeContainerClass='mx-6'
     dateRangeTitle={t('common.dateRange')}
     showDateRangeBox={true}
     filterConfig={filterConfig}
     setLoader={setLoader}
     filterFormData={filterFormData}
     filterCount={filterCount}
     setFilterFormData={setFilterFormData}
    >
     <div className='flex items-center'>
      <div
       className={`text-[#33393D] font-medium text-sm mr-2`}
      >
       {t('common.quickFilters')}
      </div>
      {categoryList.map((val, key) => (
       <div
        key={key}
        onClick={() => {
         void applyCategoryFilter(val.key)
        }}
        className={` ${
         (filterFormData?.aggregator_type
          ? filterFormData?.aggregator_type === val.key
          : val.key === '') &&
         '!bg-[#4E83E4] text-white border-[#4E83E4]'
        } text-center py-1.5 px-3 bg-white 
                 rounded-lg text-sm text-[#33393D] bg-white border font-medium border-[#CED8E0]
                 hover:bg-[#4E83E4] hover:text-white cursor-pointer mr-2 w-[90px]`}
       >
        {val.value}
       </div>
      ))}
     </div>
    </KTFilterBox>

    <div className='grid md:grid-cols-6 items-center gap-4 my-6 pb-6 border-[#E6EBF0] border-b'>
     {orderSummaryList.map((val, key) => (
      <KTSummaryCard
       key={key}
       title={val.title}
       count={
        <span>
         &#8377;{' '}
         {val?.value
          ? formatNumberForDisplay(val?.value)
          : 0}
        </span>
       }
       color={
        cardColor[key] ?? generateRandomNo(cardColor.length)
       }
      />
     ))}
    </div>

    <KTTab
     activeTabId={activeTab}
     disableHash={true}
     controlledTab={true}
     containerClass='my-6 '
     tabHeaderContainerClass='mb-6 !justify-between'
     tabHeaderClass='px-2 items-end'
     activeClass='text-[#4E83E4] font-semibold border-b-[3px] border-[#4E83E4] '
     onClick={handleTabClick}
     tabs={tabList}
     tabContent={
      <div>
       <OrderTable
        activeTabId={activeTab}
        loggedInUserId={userData?.userId}
        totalRecords={totalRecords}
        currentPage={currentPage}
        orderList={orderList}
        handlePageChange={handlePageChange}
        setAggregatorId={setAggregatorId}
        setSelectedOrder={handleSelectOrder}
        setLoader={setLoader}
        handleActionBtnClick={handleActionBtnClick}
        setSortConfig={setSortConfig}
        sortConfig={sortConfig}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
       />
      </div>
     }
    />
   </div>
  </>
 )
}

export default function OrderManagementPageWrapper() {
 const {t} = useTranslation()
 const {tabId} = useParams<{tabId: string}>()

 if (!tabId) {
  return <Navigate to={getTradeListPath()} replace />
 }

 if (!isValidTradeTab(tabId)) {
  if (/^\d+$/.test(tabId)) {
   return (
    <Navigate
     to={getTradeOrderPath(DEFAULT_TRADE_TAB, tabId)}
     replace
    />
   )
  }

  return <Navigate to={getTradeListPath()} replace />
 }

 return (
  <div className='container relative'>
   <div className='px-[72px] pt-6 pb-[200px] bg-white'>
    <PageTitle
     breadcrumbContainerClass={
      'flex text-[#5A6066] text-sm font-normal items-center pb-6 border-[#E6EBF0] border-b'
     }
     breadcrumbs={[
      {
       title: t('breadCrumbs.home'),
       path: '/home-page',
       isActive: false,
      },
      {
       title: t('breadCrumbs.trade'),
       path: '',
       isActive: true,
      },
     ]}
     description='Welcome'
    >
     {t('breadCrumbs.trade')}
    </PageTitle>
    <OrderManagementPage activeTab={tabId} />
   </div>
  </div>
 )
}
